/* ─── Phase 4: Social Feature Types ──────────────────────────────── */

import { Timestamp } from 'firebase/firestore';

// ── Notifications ────────────────────────────────────────────────────

export type NotificationType =
  | 'personal'
  | 'background'
  | 'holiday'
  | 'boosters'
  | 'message'
  | 'relationship tips'
  | 'tether_combined_notification'
  | 'tether_reply'
  | 'tether_interaction'
  | 'circle_event'
  | 'circle_message'
  | 'push notification'
  // PPL-489 — the other two conversation-starter writers. 'personal' (above)
  // is the per-person one; these are the AI-worded nudge and onboarding's
  // batch. All three route through getNotificationLink.
  | 'personal_message_triggered_push_ai'
  | 'conversation starters'
  // PPL-334 — "f8 noticed something": the write-back review nudge, written by
  // the extraction worker when it creates suggestions the user has to decide on.
  | 'writeback_review'
  // Room notifications (PPL-18..25, PPL-45)
  | 'mediation_invite'
  | 'mediation_accepted'
  | 'mediation_declined'
  | 'mediation_prep_ended'
  | 'mediation_paused'
  | 'mediation_rules_confirmed'
  | 'mediation_room_ready'
  | 'mediation_checkin'
  | 'mediation_room_open'
  | 'mediation_ended_summary'
  | 'mediation_resumed'
  | 'mediation_deleted'
  | 'mediation_reaction'
  | 'mediation_picture_request'
  // PPL-531 — types the backend has been writing that web never routed. Every
  // one of these landed on '/' (→ /f8chat) until now. `mediation_completed`
  // went the other way: it was in this union and in the route switch, and no
  // backend writer has ever sent it.
  // Calendar life (PPL-277/340): the pre-meet brief, the post-event "log it"
  // nudge, and the UNMATCHED birthday radar ping (a matched one reuses
  // 'birthday' and carries a personId).
  | 'premeet_brief'
  | 'postevent_capture'
  | 'calendar_birthday'
  // The 10am birthday flow, which predates this file.
  | 'birthday'
  // PPL-431 direct messages. 'chat_message' is a push + cache type only (no
  // rail document); 'message_request' is written to the rail with its chatId.
  | 'chat_message'
  | 'message_request'
  // PPL-460 — f8's reply to a channel message it could not answer in the
  // window. The rail entry carries the reply text itself.
  | 'channel_reply'
  // Circles: the reminder and the three invite outcomes, all carrying circleId.
  | 'circle_event_reminder'
  | 'circle_invite'
  | 'circle_invite_accepted'
  | 'circle_invite_declined'
  // Intelligent push, for the two entries with an obvious home. Its diagnostic
  // entries ('error_todo_generation_intelligent_push' and friends) are
  // deliberately absent — they fall to the route table's default.
  | 'add_people_prompt_ai'
  | 'intelligent_push_people_fallback';

export interface AppNotification {
  id: string;
  readStatus: boolean;
  type: NotificationType;
  personId?: string;
  boosterId?: string;
  /** Set on room (mediation_*) notifications so the tray can deep-link.
      Older docs predate the field — always handle its absence. */
  mediationId?: string | null;
  /** Circle notifications (events, reminders, invites) carry their circle. */
  circleId?: string | null;
  /** The invitation itself, on circle_invite / circle_invite_declined. */
  circleInviteId?: string | null;
  /**
   * PPL-531 — senders pass an event id on circle_event / circle_event_reminder,
   * but the backend's `addNotification` literal does not list the key, so it is
   * dropped today. Typed and read anyway: the routing deep-links the day the
   * server keeps it, and falls back to the circle's events list meanwhile.
   */
  eventId?: string | null;
  /** The conversation a message_request points at (PPL-431). */
  chatId?: string | null;
  /** The calendar event a brief or capture nudge is about (PPL-277). */
  eventKey?: string | null;
  /** Same story as eventId: passed by post writers, not yet persisted. */
  postId?: string | null;
  /**
   * PPL-283 / PPL-546 — a `channel_reply`'s deep link. `channel` + `linkKey`
   * name the LINK and are both persisted; `threadId` is the link's pinned f8
   * thread, which the gateway derives as `${channel}_${linkKey}` and the
   * server half of PPL-546 serves outright. `notificationRoutes` prefers the
   * served id and derives it from the pair when a rail document carries none.
   */
  channel?: string | null;
  linkKey?: string | null;
  threadId?: string | null;
  createdDate: number; // unix seconds
  title: string;
  description: string;
}

// ── Direct Messages ──────────────────────────────────────────────────

export interface Conversation {
  chatId: string;
  recipientId: string;
  recipientName: string;       // @handle — DMs are the anonymous half
  recipientPhotoURL?: string;
  lastMessage: string;
  lastMessageAt: Timestamp | number;
  unread: boolean;
  messageCount?: number;
  /**
   * PPL-428. Absent (or 'accepted') is an ordinary conversation — every one
   * that existed before requests shipped reads this way. A declined
   * conversation never reaches the person who was declined as 'declined':
   * the server reports it to them as accepted, so nothing here can tell them.
   */
  status?: ChatStatus;
  /** Who asked, when the conversation is still a request. */
  requestedBy?: string | null;
}

export type ChatStatus = 'requested' | 'accepted' | 'declined';

/**
 * A request waiting on ME (PPL-429). Everything the prompt shows and nothing
 * that could name the person: DMs are the anonymous half, so this is the
 * handle-only public profile plus the one message they sent.
 */
export interface MessageRequest {
  chatId: string;
  requestedAt: string | null;
  requester: {
    id: string;
    tetherHandle: string;
    profileImageUrl: string | null;
    city: string | null;
    aboutMe: string | null;
    /** Shared ones first — the server orders them (PPL-429). */
    interests: string[];
    /** The subset we have in common; empty when there is none. */
    sharedInterests: string[];
    lookingFor: string | null;
    /** ISO, or null when they have never been seen. */
    lastActive: string | null;
  };
  firstMessage: { text: string; sentAt: string | null } | null;
}

export interface EmojiReaction {
  emoji: string;
  userId: string;
  timestamp: Timestamp;
}

export const ALLOWED_EMOJIS = ['❤️', '😂', '😮', '😢', '😡', '👍', '👎', '🔥'] as const;

export interface ChatMessage {
  id: string;
  text: string;
  senderId: string;
  senderName: string;
  timestamp: Timestamp | number;
  type: 'user_message';
  isSpam?: boolean;
  spamType?: string;
  spamWarning?: string;
  reactions?: EmojiReaction[];
}

export interface TypingStatus {
  isTyping: boolean;
  lastUpdate: Timestamp;
  recipientId: string;
}

export interface BlockStatus {
  isBlocked: boolean;
  currentUserBlockedOther: boolean;
  otherUserBlockedCurrent: boolean;
}

// ── Tether ───────────────────────────────────────────────────────────

export type TetherStatus = 'pending' | 'delivered';

export interface Tether {
  id: string;
  message: string;
  userId: string;
  dateCreated: Timestamp | number;
  lastInteractionDate?: Timestamp | number;
  status: TetherStatus;
  sentDate?: Timestamp | number;
  recipientIds?: string[];
  likeCount: number;
  heartCount: number;
  replyCount: number;
}

export interface ReceivedTether {
  id: string;
  message: string;
  senderHandle: string;
  status: string;
  receivedDate: Timestamp | number;
  dateCreated: Timestamp | number;
  userHasLiked: boolean;
  userHasHearted: boolean;
  hasReplied: boolean;
  replyText?: string;
  replyDate?: Timestamp | number;
  likeCount: number;
  heartCount: number;
  replyCount: number;
}

export interface TetherInteraction {
  userHandle: string;
  hasLiked: boolean;
  likedAt?: Timestamp | number;
  hasHearted: boolean;
  heartedAt?: Timestamp | number;
}

export interface TetherReply {
  replyMessage: string;
  userHandle: string;
  timestamp: Timestamp | number;
}

export type TetherReactionType = 'like' | 'heart';
export type TetherReactionAction = 'add' | 'remove';

// ── Badges ───────────────────────────────────────────────────────────

export interface Badge {
  earned: boolean;
  earnedAt?: Timestamp | number;
  title: string;
  description: string;
  image: string;
}

export interface UserBadges {
  badges: Record<string, Badge>;
  totalBadges: number;
  lastUpdated?: Timestamp | number;
}

export const BADGE_DEFINITIONS: Record<string, { title: string; description: string; icon: string }> = {
  'profile-complete': { title: 'Profile Complete', description: 'Completed your full profile', icon: 'fa-user-check' },
  'verified-explorer': { title: 'Verified Explorer', description: 'Verified your location', icon: 'fa-map-marker-alt' },
  'like-champ-10': { title: 'Like Champ', description: 'Gave 10 likes', icon: 'fa-heart' },
  'like-champ-100': { title: 'Like Champion', description: 'Gave 100 likes', icon: 'fa-heart' },
  'reply-master-10': { title: 'Reply Master', description: 'Written 10 replies', icon: 'fa-reply' },
  'reply-master-100': { title: 'Reply Legend', description: 'Written 100 replies', icon: 'fa-reply-all' },
  'post-creator-10': { title: 'Post Creator', description: 'Created 10 posts', icon: 'fa-pen' },
  'post-creator-100': { title: 'Post Legend', description: 'Created 100 posts', icon: 'fa-pen-fancy' },
};

export const BADGE_ORDER = [
  'profile-complete',
  'verified-explorer',
  'like-champ-10',
  'like-champ-100',
  'reply-master-10',
  'reply-master-100',
  'post-creator-10',
  'post-creator-100',
];
